import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { docParams, DocumentOrderRealtimeStatus } from 'src/types/ICompany';
import { DOCUMENT_SUBMIT_LOADING_VALUE } from '../../../constants/companyReport';
import {
  getDocuments,
  orderDocument,
  getPreferredRegistrationNumber,
  formatDate,
} from 'src/services/companyReportDetail.service';
import { DocumentOrderUIStatus } from 'src/enums';
import { handleUBOErrors } from 'src/services/errors.service';
import { PROVIDER_SOURCES } from 'src/constants/companyReport';
import { getOrderStatusClass } from 'src/services/relevantDocument.service';
import { useAuth } from '@workos-inc/authkit-react';

import Modal from 'react-modal';

interface Props {
  activeIndex: string;
  handleOnClick: (index: string) => void;
  detailData: any;
}
const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
  },
};

const RelevantDocuments = ({ activeIndex, handleOnClick, detailData }: Props) => {
  const { getAccessToken } = useAuth();

  let [docsList, setDocsList] = useState<DocumentOrderRealtimeStatus>({});
  let [docsAreLoading, setDocsAreLoading] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  let [titleTooltip, setTitleTooltip] = useState('');
  const [modalIsOpen, setIsOpen] = useState(false);

  const [modalProductKey, setModalProductKey] = useState('');
  const [modalProductTitleField, setModalProductTitleField] = useState('');
  const [modalTierCodeField, setModalTierCodeField] = useState('');
  const [modalDateIssued, setModalDateIssued] = useState('');
  const [modalDeliveryTimeMinutesField, setModalDeliveryTimeMinutesField] = useState('');
  const [modalLoader, setModalLoader] = useState(false);

  function openModal(
    productKey: string,
    productTitleField: string,
    tierCodeField: string,
    dateIssued: string,
    deliveryTimeMinutesField: string,
  ) {
    setModalProductKey(productKey);
    setModalProductTitleField(productTitleField);
    setModalTierCodeField(tierCodeField);
    setModalDateIssued(dateIssued);
    setModalDeliveryTimeMinutesField(deliveryTimeMinutesField);
    setIsOpen(true);
  }

  function closeModal() {
    setIsOpen(false);
  }

  async function initEventSource(isClosed: () => boolean) {
    try {
      const token = await getAccessToken();
      if (isClosed()) return;
      const eventSource = new EventSource(
        `${process.env.REACT_APP_UBO_API}/documents/sse?authorization=Bearer ${encodeURIComponent(token)}`,
      );

      eventSource.onopen = (event) => {
        console.log('[I] connected.', event);
      };

      eventSource.onmessage = (event) => {
        const serverDocumentsStatus: DocumentOrderRealtimeStatus = JSON.parse(event.data);
        console.log('[I] update from server: ', serverDocumentsStatus);

        setDocsList((prevDocsList) => {
          let newList = { ...prevDocsList };

          for (let [productKey, documentStatus] of Object.entries(serverDocumentsStatus)) {
            if (productKey && prevDocsList[productKey]) {
              newList[productKey].customStatus = documentStatus?.customStatus;
              newList[productKey].urlField = documentStatus?.urlField;
              newList[productKey].productOrderIdField = documentStatus?.productOrderIdField;
            }
          }

          return newList;
        });
      };

      eventSource.onerror = (error) => {
        console.log('error server disconnected', error);
        eventSource.close();
      };

      return () => {
        eventSource.close();
      };
    } catch (error) {
      console.error("Failed to initialize EventSource:", error);
    }
  }

  useEffect(() => {
    //By default get registration number from url. i.e data sources other than DNB
    let countryISO = searchParams.get('countryISO')!;
    let stateCode = searchParams.get('stateCode')!;
    let displayRegNo = searchParams.get('displayRegNo')!;
    let companyCode: string;
    let regAuth: string = '';
    let allAvailableRegistrationNumbers =
      detailData?.companyProfile?.companyProfile?.allAvailableRegistrationNumbers;
    let companyRegistrationNumber =
      detailData?.companyProfile?.companyProfile?.companyRegistrationNumber;
    let companyName = detailData?.companyProfile?.companyProfile?.companyName;

    //In case of DNB select preferred registration number
    if (detailData?.source === PROVIDER_SOURCES.DNB && allAvailableRegistrationNumbers?.length) {
      companyCode = getPreferredRegistrationNumber(
        allAvailableRegistrationNumbers,
        companyName,
        countryISO,
      );

      regAuth = allAvailableRegistrationNumbers.filter((element: { registrationNumber: string, registrationLocation: string }) => { return element.registrationNumber === companyCode})[0].registrationLocation;
    } else if (
      detailData?.source === PROVIDER_SOURCES.KYCKR ||
      detailData?.source === PROVIDER_SOURCES.KYCKR_LITE
    ) {
      companyCode = displayRegNo;
    } else if (
      detailData?.source !== PROVIDER_SOURCES.DNB &&
      companyRegistrationNumber?.length &&
      companyRegistrationNumber[0]
    ) {
      companyCode = companyRegistrationNumber[0];
    } else {
      companyCode = companyName;
    }

    if (countryISO && companyCode) {
      if (stateCode) {
        countryISO = `${countryISO}-${stateCode}`;
      }
      setTitleTooltip(`${companyCode}:${countryISO}`);
      fetchDocuments({ countryISO, companyCode, regAuth });
    }

    let closed = false;
    let closeConnection: (() => void) | undefined;
    initEventSource(() => closed).then((close) => {
      if (closed) close?.();
      else closeConnection = close;
    });
    return () => {
      closed = true;
      closeConnection?.();
    };
  }, []);

  /**
   * Make API call for order and update state for status update
   *
   * @param productKey
   */
  const requestDocument = async (productKey: string) => {
    try {
      setModalLoader(true);
      const orderDocumentResponse = await orderDocument(productKey);

      if (orderDocumentResponse?.data?.data) {
        let updatedDoc = orderDocumentResponse?.data?.data;

        setDocsList((prevDocsList: any) => {
          for (let [productKey] of Object.entries(prevDocsList)) {
            if (productKey === updatedDoc?.productKey && prevDocsList[productKey]) {
              prevDocsList[productKey].customStatus = updatedDoc?.customStatus;
            }
          }

          return prevDocsList;
        });
      }
    } catch (error) {
      handleUBOErrors(error);
    } finally {
      setModalLoader(false);
      setIsOpen(false);
    }
  };

  /**
   * Return appropriate Action icon/button and a tooltip in case of error status
   * @param makeOrderMessage
   * @param statusField
   * @param productKey
   * @returns
   */
  const getActionOption = (
    productKey: string,
    productTitleField: string,
    tierCodeField: string,
    customStatus: string,
    dateIssued: string,
    deliveryTimeMinutesField: string,
    urlField: string,
    productFormatField: string,
    orderRef: string,
  ) => {
    if (customStatus === DocumentOrderUIStatus.Request) {
      return (
        <div>
          <button
            className="button btn-ghost sm text-uppercase"
            onClick={() =>
              openModal(
                productKey,
                productTitleField,
                tierCodeField,
                dateIssued,
                deliveryTimeMinutesField,
              )
            }
          >
            Request
          </button>
        </div>
      );
    } else if (customStatus === DocumentOrderUIStatus.Pending) {
      return '';
    } else if (customStatus === DocumentOrderUIStatus.Failed) {
      return (
        <div
          onClick={() => {
            navigator.clipboard.writeText(
              `Product Key: ${productKey}\nDocument Type: ${productFormatField}\nOrder Ref:${orderRef}`,
            );
          }}
          className="hasError"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M10.2568 5.09812C11.0218 3.73812 12.9788 3.73812 13.7428 5.09812L19.3228 15.0181C20.0728 16.3521 19.1098 17.9981 17.5808 17.9981H6.41985C4.88985 17.9981 3.92685 16.3521 4.67685 15.0181L10.2568 5.09812ZM12.9998 14.9991C12.9998 15.2643 12.8945 15.5187 12.707 15.7062C12.5194 15.8938 12.2651 15.9991 11.9998 15.9991C11.7346 15.9991 11.4803 15.8938 11.2927 15.7062C11.1052 15.5187 10.9998 15.2643 10.9998 14.9991C10.9998 14.7339 11.1052 14.4796 11.2927 14.292C11.4803 14.1045 11.7346 13.9991 11.9998 13.9991C12.2651 13.9991 12.5194 14.1045 12.707 14.292C12.8945 14.4796 12.9998 14.7339 12.9998 14.9991ZM11.9998 6.99913C11.7346 6.99913 11.4803 7.10448 11.2927 7.29202C11.1052 7.47955 10.9998 7.73391 10.9998 7.99913V10.9991C10.9998 11.2643 11.1052 11.5187 11.2927 11.7062C11.4803 11.8938 11.7346 11.9991 11.9998 11.9991C12.2651 11.9991 12.5194 11.8938 12.707 11.7062C12.8945 11.5187 12.9998 11.2643 12.9998 10.9991V7.99913C12.9998 7.73391 12.8945 7.47955 12.707 7.29202C12.5194 7.10448 12.2651 6.99913 11.9998 6.99913Z"
              fill="#E66A8B"
            />
          </svg>
          <div className="relevantDoc-tooltip">
            <p className="mb-2">
              Contact <strong>support@cleverchain.ai</strong> and provide the following details:
            </p>
            <ul>
              <li>Official Company Name</li>
              <li>Registration Number</li>
              <li>Document Type</li>
            </ul>
          </div>
        </div>
      );
    } else if (customStatus === DocumentOrderUIStatus.Success) {
      return (
        <a href={urlField} target="_blank" rel="noopener noreferrer">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M8 4C7.46957 4 6.96086 4.21071 6.58579 4.58579C6.21071 4.96086 6 5.46957 6 6V18C6 18.5304 6.21071 19.0391 6.58579 19.4142C6.96086 19.7893 7.46957 20 8 20H16C16.5304 20 17.0391 19.7893 17.4142 19.4142C17.7893 19.0391 18 18.5304 18 18V9.414C17.9999 8.88361 17.7891 8.37499 17.414 8L14 4.586C13.625 4.2109 13.1164 4.00011 12.586 4H8ZM13 10C13 9.73478 12.8946 9.48043 12.7071 9.29289C12.5196 9.10536 12.2652 9 12 9C11.7348 9 11.4804 9.10536 11.2929 9.29289C11.1054 9.48043 11 9.73478 11 10V13.586L9.707 12.293C9.61475 12.1975 9.50441 12.1213 9.3824 12.0689C9.2604 12.0165 9.12918 11.9889 8.9964 11.9877C8.86362 11.9866 8.73194 12.0119 8.60905 12.0622C8.48615 12.1125 8.3745 12.1867 8.2806 12.2806C8.18671 12.3745 8.11246 12.4861 8.06218 12.609C8.0119 12.7319 7.9866 12.8636 7.98775 12.9964C7.9889 13.1292 8.01649 13.2604 8.0689 13.3824C8.12131 13.5044 8.19749 13.6148 8.293 13.707L11.293 16.707C11.4805 16.8945 11.7348 16.9998 12 16.9998C12.2652 16.9998 12.5195 16.8945 12.707 16.707L15.707 13.707C15.8892 13.5184 15.99 13.2658 15.9877 13.0036C15.9854 12.7414 15.8802 12.4906 15.6948 12.3052C15.5094 12.1198 15.2586 12.0146 14.9964 12.0123C14.7342 12.01 14.4816 12.1108 14.293 12.293L13 13.586V10Z"
              fill="#11BC97"
            />
          </svg>
        </a>
      );
    } else {
      return '-';
    }
  };

  const fetchDocuments = async (params: docParams) => {
    setDocsAreLoading(true);
    try {
      let docsList = await getDocuments(
        {
        companyCode: params.companyCode,
        countryISO: params.countryISO,
      },
      params.regAuth
    );

      if (Array.isArray(docsList?.data?.data)) {
        let list: DocumentOrderRealtimeStatus = {};
        for (let doc of docsList.data.data) {
          if (doc.productKey) {
            list[doc.productKey] = doc;
          }
        }
        setDocsList(list);
      } else {
        console.error('invalid docsList format returned.', docsList);
      }
    } catch (err) {
      console.log('[E] error getting docs', err);
      // CLEV-1235
      // handleUBOErrors(err);
    } finally {
      setDocsAreLoading(false);
    }
  };

  return (
    <div
      className={`card accordion ${docsAreLoading ? 'loading' : ''} ${
        activeIndex === 'relevant-doc' ? 'active' : ''
      }`}
    >
      <div className="accordion-heading" onClick={() => handleOnClick('relevant-doc')}>
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex gap-4 align-items-center">
            <h4 className="mb-0" title={titleTooltip}>
              RELEVANT DOCUMENTS <span>({Object.keys(docsList).length})</span>
            </h4>
          </div>
          <div>
            <img
              className="chevron-icon"
              src={require('src/assets/images/icons/chevron-down.svg').default}
              alt="chevron-down icon"
            />
          </div>
        </div>
      </div>
      <div className="accordion-content">
        <div className="mt-3">
          <table className="accordion-table sm-th-md-td relevent-docs">
            <thead>
              <tr>
                <th>Document Type</th>
                <th>Order Number</th>
                <th>Pricing Tier</th>
                <th>Date Issued</th>
                <th>Retrieval Time</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(docsList).map(([productKey, documentObject]) => (
                <tr key={productKey} className="hvg-bg">
                  <td title={documentObject.productKey}>
                    {documentObject.productTitleField ?? '-'}
                  </td>
                  <td>{(documentObject.productOrderIdField || '-') ?? '-'}</td>
                  <td>{documentObject.tierCodeField ?? '-'}</td>
                  <td>{formatDate(documentObject.dateIssued)}</td>
                  <td>
                    {documentObject.deliveryTimeMinutesField ?? '-'}{' '}
                    {documentObject.deliveryTimeMinutesField ? 'Minutes' : ''}
                  </td>
                  <td>
                    {documentObject?.customStatus === DocumentOrderUIStatus.Request || (
                      <span className={getOrderStatusClass(documentObject?.customStatus)}>
                        {documentObject?.customStatus}
                      </span>
                    )}
                  </td>
                  <td>
                    {getActionOption(
                      productKey,
                      documentObject?.productTitleField,
                      documentObject?.tierCodeField,
                      documentObject?.customStatus,
                      documentObject?.dateIssued,
                      documentObject?.deliveryTimeMinutesField,
                      documentObject?.urlField,
                      documentObject?.productFormatField,
                      documentObject?.orderRef,
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Modal
            isOpen={modalIsOpen}
            // onAfterOpen={afterOpenModal}
            onRequestClose={closeModal}
            style={customStyles}
            contentLabel="Example Modal"
            ariaHideApp={false}
          >
            <div className="confirm-popup">
              <div className="row">
                <div className="col-lg-12 px-4">
                  <h4 className="color-monoIntense">Review document request</h4>
                  <p className="color-monoIntense">
                    Please review document request before confirming the purchase
                  </p>
                </div>
              </div>
              <table className="accordion-table sm-th-md-td relevent-docs">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>PricingTier</th>
                    <th>Date Issued</th>
                    <th>Retrieval Time</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-0">
                    <td>{modalProductTitleField ?? '-'}</td>
                    <td>{modalTierCodeField ?? '-'}</td>
                    <td>{formatDate(modalDateIssued)}</td>
                    <td>
                      {modalDeliveryTimeMinutesField ?? '-'}{' '}
                      {modalDeliveryTimeMinutesField && 'Minutes'}
                    </td>
                  </tr>
                </tbody>
              </table>
              <div className="button-group text-right mt-12">
                <button onClick={closeModal} type="button" className="button btn-ghost">
                  Cancel
                </button>
                <button
                  onClick={() => requestDocument(modalProductKey)}
                  type="button"
                  className="button btn-with-icon"
                  disabled={modalLoader}
                >
                  {modalLoader ? DOCUMENT_SUBMIT_LOADING_VALUE : 'Confirm'}
                </button>
              </div>
            </div>
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default RelevantDocuments;
